
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type ReportReason = 
  | 'spam'
  | 'harassment'
  | 'inappropriate_content'
  | 'misinformation'
  | 'copyright_violation'
  | 'hate_speech'
  | 'violence'
  | 'other';

export type ContentType = 'post' | 'cast' | 'comment';

export interface ContentReport {
  id: string;
  reporter_id: string;
  content_id: string;
  content_type: ContentType;
  reason: ReportReason;
  description?: string;
  status: 'pending' | 'reviewed' | 'resolved' | 'dismissed';
  created_at: string;
}

export const useContentReports = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitReport = async (
    contentId: string,
    contentType: ContentType,
    reason: ReportReason,
    description?: string
  ) => {
    try {
      setIsSubmitting(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to report content');
        return false;
      }

      // Use direct table insert instead of RPC
      const { error } = await supabase
        .from('content_reports')
        .insert({
          reporter_id: user.id,
          content_id: contentId,
          content_type: contentType,
          reason: reason,
          description: description || null
        });

      if (error) {
        if (error.message?.includes('duplicate') || error.code === '23505') {
          toast.error('You have already reported this content');
        } else {
          console.error('Error submitting report:', error);
          toast.error('Failed to submit report. Please try again.');
        }
        return false;
      }

      toast.success('Content reported successfully. Thank you for helping keep our community safe.');
      return true;
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error('Failed to submit report. Please try again.');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const checkIfReported = async (contentId: string, contentType: ContentType) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      // Use direct table query instead of RPC
      const { data, error } = await supabase
        .from('content_reports')
        .select('id')
        .eq('reporter_id', user.id)
        .eq('content_id', contentId)
        .eq('content_type', contentType)
        .limit(1);

      if (error) {
        console.error('Error checking report status:', error);
        return false;
      }

      return data && data.length > 0;
    } catch (error) {
      console.error('Error checking report status:', error);
      return false;
    }
  };

  return {
    submitReport,
    checkIfReported,
    isSubmitting
  };
};
